import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect, useLocation, useHistory } from 'react-router-dom';
import { AppLayout, TopNavigation, SideNavigation } from '@cloudscape-design/components';
import { useAuth } from './hooks/useAuth';
import Auth from './components/auth/Auth';
import FundingRequestTracker from './pages/FundingRequestTracker';
import FundingRequestDetail from './pages/FundingRequestDetail';
import FeedbackReport from './pages/FeedbackReport';
import AuditReport from './pages/AuditReport';
import SubmitFeedback from './pages/SubmitFeedback';
import { logVisit } from './services/s3Service';
import CalculatorCheck from './pages/CalculatorCheck';

import { isFundRequestDetailsPage } from './utils/urlHelper';
import Breadcrumbs from './components/Breadcrumbs';



function MainApp() {
  const { isAuthenticated, user, loading: authLoading, login, logout } = useAuth();
  const [navigationOpen, setNavigationOpen] = useState(true);
  const location = useLocation();
  const history = useHistory();

  // Log visit once per browser session
  useEffect(() => {
    if (isAuthenticated && !sessionStorage.getItem('visit_logged')) {
      sessionStorage.setItem('visit_logged', 'true');
      logVisit();
    }
  }, [isAuthenticated]);

  const handleChronosClick = (e) => {
    e.preventDefault();
    history.push('/dashboard');
  };

  if (authLoading) {
    return null;
  }

  // FIXED: Don't auto-call login() here - let useAuth handle redirects
  // Allow /auth route to render even when not authenticated
  if (!isAuthenticated && location.pathname !== '/auth') {
    return null;
  }





  const getActiveHref = () => {
    const { pathname } = location;
    if (pathname === '/calculator-check') return '/calculator-check';
    if (pathname === '/event-history') return '/event-history';
    if (pathname === '/reporting') return '/reporting';
    if (pathname === '/submit-feedback') return '/submit-feedback';
    return '/dashboard';
  };

  const navigationItems = [
    { type: 'link', text: 'Validated Fund Requests', href: '/dashboard' },
    { type: 'link', text: 'Review Calculator', href: '/calculator-check' },
    { type: 'link', text: 'Event History', href: '/event-history' },
    { type: 'link', text: 'Reporting', href: '/reporting' },
    { type: 'link', text: 'Submit Feedback', href: '/submit-feedback' },
    { type: 'divider' },
    {
      type: 'section',
      text: 'Links of interest',
      items: [
        { type: 'link', text: 'About Chronos', href: 'https://w.amazon.com/bin/view/MigrationPSATeam/Chronos/', external: true },
        { type: 'link', text: 'User Guide', href: 'https://amazon.awsapps.com/workdocs-amazon/index.html#/document/341df4f2d973714ada4d80e5d731a5d713fd80c7db5e9c4ab970e18ea8078fdb', external: true },
        { type: 'link', text: '#chronos-interest', href: 'https://amazon.enterprise.slack.com/archives/C0AHD16JZ7B', external: true }
      ]
    }
  ];

  return (
    <>
      <TopNavigation
        identity={{
          href: '#',
          title: 'Chronos',
          logo: {
            src: '/logo.png',
            alt: 'Chronos Logo',
          },
          onFollow: handleChronosClick
        }}
        utilities={[
          {
            type: 'button',
            text: `${user?.isAdmin ? 'Admin / ' : ''}${user?.email ? user.email.split('@')[0] : user?.name || 'User'}`,
            description: user?.email || '',
            iconName: 'user-profile'
          },
          {
            type: 'button',
            text: 'Refresh Credentials',
            onClick: logout
          }
        ]}
      />
      <AppLayout
        navigationOpen={navigationOpen}
        navigationHide={isFundRequestDetailsPage()}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        navigation={
          !isFundRequestDetailsPage() ? (
            <SideNavigation
              activeHref={getActiveHref()}
              header={{ text: 'Chronos', href: '/dashboard' }}
              items={navigationItems}
              onFollow={event => {
                if (event.detail.external) {
                  return;
                }
                event.preventDefault();
                const href = event.detail.href;
                history.push(href);
              }}
            />
          ) : null
        }
        toolsHide
        contentType="default"
        maxContentWidth={Number.MAX_VALUE}
        breadcrumbs={<Breadcrumbs />}
        content={
          <Switch>
            <Route exact path="/auth">
              <Auth />
            </Route>
            <Route exact path="/dashboard">
              <FundingRequestTracker />
            </Route>
            <Route exact path="/fund-requests/:id/details">
              <FundingRequestDetail />
            </Route>
            <Route exact path="/event-history">
              <AuditReport />
            </Route>
            <Route exact path="/reporting">
              <FeedbackReport />
            </Route>
            <Route exact path="/submit-feedback">
              <SubmitFeedback />
            </Route>
            <Route exact path="/calculator-check">
              <CalculatorCheck />
            </Route>
            <Route path="*">
              <Redirect to="/dashboard" />
            </Route>
          </Switch>
        }
      />
    </>
  );
}

function App() {
  return (
    <Router>
      <MainApp />
    </Router>
  );
}

export default App;
